import API_URLS from "@/constants/apiUrls";
import { HTTP_METHODS } from "@/constants/httpMethods";
import { request } from "@/lib/api";

/**
 * Car City Service
 * Araç şehir sayfaları içerik servisleri
 */
export const carCityService = {

  /**
   * Şehir bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "Slug": "istanbul"
   * }
   */
  getCityLocationInfo: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getCityLocationInfo,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm kampanyaları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   * }
   */
  getCampaigns: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getCampaigns,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralık araç listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "CurrencyCode": "TRY"
   * }
   */
  getPopularCars: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getPopularCars,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Şehir analytics bilgilerini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   *   "CurrencyCode": "TRY"
   * }
   */
  getCityAnalytics: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getCityAnalytics,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler kiralama noktaları listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getRentalLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getRentalLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Tüm ayrıcalıkları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getPrivileges: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getPrivileges,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler firma listesini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "TR"
   * }
   */
  getPopularCompanies: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getCompanies,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Popüler tatil bölgelerini getirir
   * @param {Object} payload - API payload
   * @param {string} [payload.LanguageCode] - Dil kodu (örn: "tr", "en")
   * @param {string} [payload.CurrencyCode] - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Özel hata yönetimi kullanılsın mı
   * @returns {Promise<any>} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getPopularLocations: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getLocations,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Blog yazılarını getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.ClientChannel - İstemci kanalı (örn: "web", "mobile")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "ClientChannel": "web",
   *   "CurrencyCode": "TRY"
   * }
   */
  getBlog: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getBlog,
      payload,
      headers,
      hasErrorHandler,
    }),


  /**
   * Neden Turna sayfası içeriğini getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr"
   * }
   */
  getWhyTurna: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getWhyTurna,
      payload,
      headers,
      hasErrorHandler,
    }),

  /**
   * Sık sorulan soruları getir
   * @param {Object} payload - API payload
   * @param {string} payload.LanguageCode - Dil kodu (örn: "tr", "en")
   * @param {string} payload.CurrencyCode - Para birimi kodu (örn: "TRY", "USD")
   * @param {Object} headers - HTTP headers
   * @param {boolean} hasErrorHandler - Hata yönetimi aktif mi
   * @returns {Promise} API response
   *
   * Payload Example:
   * {
   *   "LanguageCode": "tr",
   *   "CurrencyCode": "TRY"
   * }
   */
  getSss: (payload = {}, headers = {}, hasErrorHandler = false) =>
    request({
      method: HTTP_METHODS.POST,
      url: API_URLS.car.city.getSss,
      payload,
      headers,
      hasErrorHandler,
    }),
};

